<!-- ER-6829: TargetsTypePicker Component -->
<template>
  <div class="targets-type-picker" ref="pickerContainer">
    <label v-if="label" class="targets-type-picker__label">{{ label }}</label>
    <!-- Closed State / Trigger -->
    <div
      class="targets-type-picker__trigger"
      :class="{
        'targets-type-picker__trigger--open': isOpen,
        'targets-type-picker__trigger--disabled': disabled,
      }"
      @click="toggleDropdown"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter="toggleDropdown"
      @keydown.space.prevent="toggleDropdown"
      @keydown.escape="closeDropdown"
      role="combobox"
      :aria-expanded="isOpen"
      :aria-disabled="disabled ? 'true' : 'false'"
      :aria-haspopup="'listbox'"
      :aria-label="label || 'Target type'"
    >
      <div class="targets-type-picker__content">
        <TargetsTypeIcon :icon="selectedType" class="targets-type-picker__icon" />
        <span class="targets-type-picker__label-value">{{ typeLabel }}</span>
      </div>
      <v-icon
        class="targets-type-picker__chevron"
        :class="{ 'targets-type-picker__chevron--rotated': isOpen }"
      >
        mdi-chevron-down
      </v-icon>
    </div>

    <!-- Dropdown Menu -->
    <div
      v-if="isOpen"
      class="targets-type-picker__dropdown"
      role="listbox"
      :aria-label="`Select ${label || 'target type'}`"
    >
      <div class="targets-type-picker__menu">
        <div
          v-for="option in targetTypeOptions"
          :key="option.value"
          class="targets-type-picker__menu-item"
          :class="{
            'targets-type-picker__menu-item--selected': selectedType === option.value,
          }"
          @click="selectType(option.value)"
          tabindex="0"
          @keydown.enter="selectType(option.value)"
          role="option"
          :aria-selected="selectedType === option.value"
        >
          <div class="targets-type-picker__menu-item-content">
            <TargetsTypeIcon
              :icon="option.value"
              class="targets-type-picker__menu-icon"
            />
            <span class="targets-type-picker__menu-label">{{ option.label }}</span>
          </div>
          <img
            v-if="selectedType === option.value && !showProhibitedIcon[option.value]"
            src="@components/assets/rightIcon.svg"
            alt=""
            class="targets-type-picker__check-icon"
            aria-hidden="true"
          />
          <v-icon
            v-if="showProhibitedIcon[option.value]"
            class="targets-type-picker__prohibited-icon"
            aria-hidden="true"
          >
            mdi-close-circle
          </v-icon>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import TargetsTypeIcon from "./TargetsTypeIcon.vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "Fixed",
    validator: (value) => ["Fixed", "Prorated"].includes(value),
  },
  label: {
    type: String,
    default: "Target Type",
  },
  open: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "update:open", "change"]);

const pickerContainer = ref(null);
const isOpen = ref(props.open);
const showProhibitedIcon = ref({
  Fixed: false,
  Prorated: false,
});

const targetTypeOptions = [
  { value: "Fixed", label: "Fixed" },
  { value: "Prorated", label: "Prorated" },
];

const selectedType = ref(props.modelValue);

const typeLabel = computed(() => {
  const option = targetTypeOptions.find(
    (opt) => opt.value === selectedType.value
  );
  return option ? option.label : "Fixed";
});

watch(
  () => props.modelValue,
  (newVal) => {
    selectedType.value = newVal;
  }
);

watch(
  () => props.open,
  (newVal) => {
    isOpen.value = newVal;
  }
);

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  emit("update:open", isOpen.value);
};

const closeDropdown = () => {
  isOpen.value = false;
  emit("update:open", false);
};

const selectType = (typeValue) => {
  // If clicking on already selected option, show prohibited icon
  if (selectedType.value === typeValue) {
    showProhibitedIcon.value[typeValue] = true;
    // Hide prohibited icon after 1 second
    setTimeout(() => {
      showProhibitedIcon.value[typeValue] = false;
    }, 1000);
    return; // Don't change selection or close dropdown
  }
  
  // Reset prohibited icons when selecting a different option
  Object.keys(showProhibitedIcon.value).forEach((key) => {
    showProhibitedIcon.value[key] = false;
  });
  
  selectedType.value = typeValue;
  emit("update:modelValue", typeValue);
  emit("change", typeValue);
  closeDropdown();
};

const handleClickOutside = (event) => {
  if (pickerContainer.value && !pickerContainer.value.contains(event.target)) {
    closeDropdown();
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.targets-type-picker {
  position: relative;
  width: 184px;
  font-family: Inter, sans-serif;
}

.targets-type-picker__label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  margin-bottom: 6px;
}

.targets-type-picker__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 14px;
  background-color: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;
  outline: none;
}

.targets-type-picker__trigger:focus,
.targets-type-picker__trigger--open {
  border-color: #d6bbfb;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05),
    0px 0px 0px 4px rgba(158, 119, 237, 0.24);
}

.targets-type-picker__trigger--disabled,
.targets-type-picker__trigger--disabled:focus {
  background-color: #f9fafb;
  border-color: #d0d5dd;
  box-shadow: none;
  cursor: not-allowed;
}

.targets-type-picker__content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.targets-type-picker__icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.targets-type-picker__label-value {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.targets-type-picker__chevron {
  flex-shrink: 0;
  color: #667085 !important;
  font-size: 20px !important;
  transition: transform 0.2s ease;
}

.targets-type-picker__chevron--rotated {
  transform: rotate(180deg);
}

.targets-type-picker__dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 1000;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08),
    0px 4px 6px -2px rgba(16, 24, 40, 0.03);
  overflow: hidden;
}

.targets-type-picker__menu {
  padding: 4px 0;
}

.targets-type-picker__menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 10px 10px 8px;
  margin: 1px 6px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  outline: none;
}

.targets-type-picker__menu-item:hover {
  background-color: #f9fafb;
}

.targets-type-picker__menu-item:focus {
  background-color: #f9fafb;
}

.targets-type-picker__menu-item--selected {
  background-color: #f9fafb;
}

.targets-type-picker__menu-item-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.targets-type-picker__menu-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
}

.targets-type-picker__menu-label {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #101828;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.targets-type-picker__check-icon {
  flex-shrink: 0;
  width: 16px;
  height: 11px;
  object-fit: contain;
}

.targets-type-picker__prohibited-icon {
  flex-shrink: 0;
  color: #d92d20 !important;
  font-size: 20px !important;
  animation: shake 0.3s ease-in-out;
}

@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-4px);
  }
  75% {
    transform: translateX(4px);
  }
}
</style>
